import type BottomSheet from '@gorhom/bottom-sheet';
import { useCallback, useRef } from 'react';
import { Keyboard, Platform } from 'react-native';

/**
 * Keyboard handling for a bottom sheet holding our `<Input>` fields (the
 * Profile edit sheets). Pass `textInputComponent={BottomSheetTextInput}` to
 * each Input: gorhom's `keyboardBehavior="fillParent"` only follows inputs it
 * knows about, and then it tracks the real keyboard height on any device and
 * text size. A plain TextInput left the sheet at its first snap point with the
 * keyboard over the fields and Save. Use BottomSheetScrollView for the body:
 * at the largest accessibility text sizes the form is taller than the space
 * above the keyboard, and scrolling is the only way to reach Save.
 *
 * - `expandOnFocus`: pass as each field's `onFocus`. Native needs nothing
 *   (gorhom handles it). Phone browsers get no keyboard events, so there the
 *   sheet snaps to its LAST snap point to sit above the keyboard; desktop
 *   browsers have no on-screen keyboard and are left alone.
 * - `handleChange`: pass as the sheet's `onChange`; on close it dismisses the
 *   keyboard, then calls `onClose`.
 * - `close`: animate the sheet shut after a successful save (ends in
 *   `handleChange`). A no-op once this sheet is unmounted, so a slow request
 *   from a dismissed sheet can't close a newly opened one.
 */
export function useFormSheet(onClose: () => void, lastSnapIndex: number) {
  const sheetRef = useRef<BottomSheet>(null);

  const expandOnFocus = useCallback(() => {
    if (!isTouchBrowser()) return;
    sheetRef.current?.snapToIndex(lastSnapIndex);
  }, [lastSnapIndex]);

  const handleChange = useCallback(
    (index: number) => {
      if (index !== -1) return;
      Keyboard.dismiss();
      onClose();
    },
    [onClose],
  );

  const close = useCallback(() => sheetRef.current?.close(), []);

  return { sheetRef, expandOnFocus, handleChange, close };
}

// A phone or tablet browser: an on-screen keyboard will cover the bottom of
// the page. `pointer: coarse` is the primary input being a finger.
function isTouchBrowser(): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches;
}
